import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/client';
import EquityChart from '../components/EquityChart';
import BenchmarkPanel from '../components/BenchmarkPanel';
import MetricInfo from '../components/MetricInfo';
import MonthlyHeatmap from '../components/MonthlyHeatmap';
import DrawdownChart from '../components/DrawdownChart';
import RollingSharpChart from '../components/RollingSharpChart';
import VixChart from '../components/VixChart';
import ReturnsHistogram from '../components/ReturnsHistogram';
import ExitTypeAnalysis from '../components/ExitTypeAnalysis';
import { returnsHistogram, exitTypeStats } from '../lib/analytics';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NativeSelect } from '../components/ui/native-select';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Play, Activity, Code2, Settings2, X, ChevronUp, ChevronDown, Loader2 } from 'lucide-react';

const today = () => new Date().toISOString().slice(0, 10);
const DEFAULT_START = '2020-01-01';

const ENTRY_OPTIONS = [
  { value: 'next_open',    label: 'Next day open' },
  { value: 'signal_close', label: 'Signal day close' },
];
const SIZING_OPTIONS = [
  { value: 'equal_weight', label: 'Equal weight' },
  { value: 'fixed_amount', label: 'Fixed amount (₹)' },
  { value: 'fixed_pct',    label: 'Fixed % of portfolio' },
  { value: 'volatility',   label: 'Volatility-based (ATR)' },
];

const RUN_STAGES = [
  'Checking signal backfill…',
  'Loading historical prices…',
  'Simulating trades day by day…',
  'Computing equity curve…',
  'Crunching analytics…',
];

const DEFAULT_CONFIG = {
  initial_funds:       100000,
  max_positions:       5,
  entry:               'next_open',
  stop_loss_pct:       '',
  take_profit_pct:     '',
  trailing_stop_pct:   '',
  max_holding_days:    '',
  atr_stop_multiplier: '',
  atr_stop_mode:       'fixed',
  atr_stop_period:     14,
  exit_slippage_pct:   '',
  position_sizing:     'equal_weight',
  position_size_value: '',
  vol_lookback:        14,
  vol_atr_multiplier:  2.0,
  start_date:          DEFAULT_START,
  end_date:            today(),
};

function Metric({ label, value, sub, pos, neg, info, subInfo }) {
  // Added subtle background tints for positive/negative values
  const colorClass = pos ? 'text-win' : neg ? 'text-loss' : 'text-foreground';
  const bgClass = pos ? 'bg-win/10' : neg ? 'bg-loss/10' : 'bg-muted/10';

  return (
    <Card className={`flex flex-col p-4 rounded-xl shadow-xs hover:shadow-md transition-shadow border-border/60 ${bgClass}`}>
      <CardContent className="flex flex-col flex-1 p-0 justify-center">
        <div className="text-sm font-medium text-muted-foreground mb-1 flex items-center gap-1">
          {label}{info && <MetricInfo metric={info} />}
        </div>
        <div className={`text-2xl font-bold tracking-tight ${colorClass}`}>{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">{sub}{subInfo && <MetricInfo metric={subInfo} />}</div>}
      </CardContent>
    </Card>
  );
}

function ConfigLabel({ children, label }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-foreground/80">{label}</label>
      {children}
    </div>
  );
}

function pct(v) {
  if (v == null) return '—';
  return `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;
}

function money(v) {
  if (v == null) return '—';
  if (v >= 1_00_00_000) return `₹${(v / 1_00_00_000).toFixed(2)} Cr`;
  if (v >= 1_00_000)    return `₹${(v / 1_00_000).toFixed(2)} L`;
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}


export default function Backtest() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [screener, setScreener]   = useState(null);
  const [config, setConfig]       = useState(DEFAULT_CONFIG);
  const [running, setRunning]     = useState(false);
  const [runStage, setRunStage]   = useState(0);
  const [runPct, setRunPct]       = useState(0);
  const [result, setResult]       = useState(null);
  const [error, setError]         = useState('');
  const [tradeSort, setTradeSort] = useState({ col: 'entry_date', dir: 1 });
  const [tradePage, setTradePage] = useState(0);
  
  // Mobile UI State
  const [isMobileConfigOpen, setIsMobileConfigOpen] = useState(false);

  const TRADES_PER_PAGE = 20;

  useEffect(() => {
    api.get('/screeners').then((r) => {
      const s = r.data.find((x) => x.id === parseInt(id));
      setScreener(s || null);
      if(s && s.backtest_config) {
        // The backend response for backtest_config both omits some keys entirely
        // (e.g. exit_slippage_pct when unset) and returns `null` for others (e.g.
        // stop_loss_pct). These fields are bound to controlled <Input> elements
        // below, so either case — an absent key or a literal `null` — flips the
        // input from controlled to uncontrolled and triggers a React warning.
        // Layer onto DEFAULT_CONFIG (which has '' for every nullable field) so
        // omitted keys keep their default, then normalize any explicit null.
        const sanitized = Object.fromEntries(
          Object.entries(s.backtest_config).map(([k, v]) => [k, v === null ? '' : v])
        );
        setConfig({...DEFAULT_CONFIG, ...sanitized,
          start_date: DEFAULT_START,
          end_date:   today(),
        });
      }
    });
  }, [id]);

  // No run-progress endpoint exists on the backend for a single screener
  // backtest (it's one synchronous request/response) — this animates a
  // staged, asymptotic progress indicator client-side so the wait doesn't
  // read as a frozen page. It never reaches 100%; the view switches to the
  // real result as soon as the request resolves.
  useEffect(() => {
    if (!running) { setRunStage(0); setRunPct(0); return; }
    const stageTimer = setInterval(() => {
      setRunStage((s) => Math.min(s + 1, RUN_STAGES.length - 1));
    }, 1800);
    const pctTimer = setInterval(() => {
      setRunPct((p) => (p >= 92 ? 92 : p + (92 - p) * 0.08 + 0.3));
    }, 200);
    return () => { clearInterval(stageTimer); clearInterval(pctTimer); };
  }, [running]);

  const set = (k, v) => setConfig((c) => ({ ...c, [k]: v }));

  const handleRun = async () => {
    setRunning(true);
    setResult(null);
    setError('');
    setTradePage(0);
    setIsMobileConfigOpen(false); // Close mobile drawer on run
    try {
      const body = {
        ...config,
        initial_funds:       Number(config.initial_funds),
        max_positions:       Number(config.max_positions),
        stop_loss_pct:       config.stop_loss_pct       ? Number(config.stop_loss_pct)       : null,
        take_profit_pct:     config.take_profit_pct     ? Number(config.take_profit_pct)     : null,
        trailing_stop_pct:   config.trailing_stop_pct   ? Number(config.trailing_stop_pct)   : null,
        max_holding_days:    config.max_holding_days     ? Number(config.max_holding_days)    : null,
        atr_stop_multiplier: config.atr_stop_multiplier  ? Number(config.atr_stop_multiplier) : null,
        atr_stop_mode:       config.atr_stop_mode || 'fixed',
        atr_stop_period:     Number(config.atr_stop_period) || 14,
        exit_slippage_pct:   config.exit_slippage_pct    ? Number(config.exit_slippage_pct)   : 0,
        position_size_value: config.position_size_value  ? Number(config.position_size_value) : 0,
        vol_lookback:        Number(config.vol_lookback),
        vol_atr_multiplier:  Number(config.vol_atr_multiplier),
      };
      const res = await api.post(`/backtest/${id}`, body);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Backtest failed');
    } finally {
      setRunning(false);
    }
  };

  const sortedTrades = result
    ? [...result.trades].sort((a, b) => {
        const av = a[tradeSort.col] ?? '';
        const bv = b[tradeSort.col] ?? '';
        return av < bv ? -tradeSort.dir : av > bv ? tradeSort.dir : 0;
      })
    : [];

  const pagedTrades  = sortedTrades.slice(tradePage * TRADES_PER_PAGE, (tradePage + 1) * TRADES_PER_PAGE);
  const totalPages   = Math.ceil(sortedTrades.length / TRADES_PER_PAGE);

  const onSort = (col) => setTradeSort((s) => ({ col, dir: s.col === col ? -s.dir : 1 }));

  const sortIcon = (col) => {
    if (tradeSort.col !== col) return null;
    return tradeSort.dir === 1 ? <ChevronUp className="w-3 h-3 inline ml-1" /> : <ChevronDown className="w-3 h-3 inline ml-1" />;
  };

  const m = result?.metrics;

  return (
    <div className="flex flex-col h-screen bg-background overflow-hidden">
      
      {/* Header */}
      <header className="flex-none flex items-center justify-between h-14 px-4 bg-card border-b border-border z-10">
        <div className="flex items-center gap-2 overflow-hidden">
          <Button variant="ghost" size="icon" onClick={() => navigate('/screeners')} className="shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 truncate">
            <span className="font-semibold text-sm text-foreground truncate">{screener?.name || 'Loading…'}</span>
            <span className="hidden sm:inline text-muted-foreground text-sm">/</span>
            <span className="text-xs sm:text-sm text-muted-foreground">Backtest</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => navigate(`/signals/${id}`)} className="hidden sm:flex gap-1.5">
            <Activity className="w-4 h-4" /> Signals
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate(`/builder/${id}`)} className="hidden sm:flex gap-1.5">
            <Code2 className="w-4 h-4" /> Edit code
          </Button>
          {/* Mobile Config Toggle */}
          <Button variant="outline" size="icon" className="md:hidden" onClick={() => setIsMobileConfigOpen(!isMobileConfigOpen)}>
            {isMobileConfigOpen ? <X className="w-5 h-5" /> : <Settings2 className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden relative">
        
        {/* Config Panel (Sidebar on Desktop, Full-Screen Overlay on Mobile) */}
        <aside className={`
          absolute inset-y-0 left-0 z-40 w-full bg-card border-r border-border overflow-y-auto p-5 flex flex-col gap-6 shadow-xl
          transform transition-transform duration-300 ease-in-out md:relative md:w-80 md:translate-x-0 md:shadow-none
          ${isMobileConfigOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <div className="flex items-center justify-between md:hidden">
            <h3 className="text-sm font-semibold text-foreground">Configuration</h3>
            <Button variant="ghost" size="icon" onClick={() => setIsMobileConfigOpen(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <h3 className="hidden md:block text-xs font-bold uppercase tracking-wider text-muted-foreground">Configuration</h3>

          <div className="flex flex-col gap-4">
            <h4 className="text-sm font-semibold text-foreground border-b border-border pb-1">Capital</h4>
            <ConfigLabel label="Initial funds (₹)">
              <Input type="number" value={config.initial_funds} min="1000" className="h-9" onChange={(e) => set('initial_funds', e.target.value)} />
            </ConfigLabel>
            <ConfigLabel label="Max open positions">
              <Input type="number" value={config.max_positions} min="1" max="50" className="h-9" onChange={(e) => set('max_positions', e.target.value)} />
            </ConfigLabel>
          </div>

          <div className="flex flex-col gap-4">
            <h4 className="text-sm font-semibold text-foreground border-b border-border pb-1">Entry</h4>
            <ConfigLabel label="Strategy">
              <NativeSelect value={config.entry} onChange={(e) => set('entry', e.target.value)} className="h-9 py-1.5">
                {ENTRY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </NativeSelect>
            </ConfigLabel>
          </div>

          <div className="flex flex-col gap-4">
            <h4 className="text-sm font-semibold text-foreground border-b border-border pb-1 flex justify-between items-baseline">
              Exit <span className="text-xs font-normal text-muted-foreground">(blank = hold)</span>
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <ConfigLabel label="Stop loss %">
                <Input type="number" value={config.stop_loss_pct} min="0" step="0.5" placeholder="e.g. 5" className="h-9" onChange={(e) => set('stop_loss_pct', e.target.value)} />
              </ConfigLabel>
              <ConfigLabel label="Take profit %">
                <Input type="number" value={config.take_profit_pct} min="0" step="0.5" placeholder="e.g. 15" className="h-9" onChange={(e) => set('take_profit_pct', e.target.value)} />
              </ConfigLabel>
              <ConfigLabel label="Trail stop %">
                <Input type="number" value={config.trailing_stop_pct} min="0" step="0.5" placeholder="e.g. 8" className="h-9" onChange={(e) => set('trailing_stop_pct', e.target.value)} />
              </ConfigLabel>
              <ConfigLabel label="Max days">
                <Input type="number" value={config.max_holding_days} min="1" placeholder="e.g. 20" className="h-9" onChange={(e) => set('max_holding_days', e.target.value)} />
              </ConfigLabel>
              <ConfigLabel label="Exit slippage %">
                <Input type="number" value={config.exit_slippage_pct} min="0" step="0.1" placeholder="e.g. 0.3" className="h-9" onChange={(e) => set('exit_slippage_pct', e.target.value)} />
              </ConfigLabel>
            </div>

            <div className="grid grid-cols-2 gap-3 p-3 bg-muted/30 rounded-lg border border-border/50">
              <div className="col-span-2 text-xs font-medium text-muted-foreground">ATR stop (blank = off)</div>
              <ConfigLabel label="ATR multiplier">
                <Input type="number" value={config.atr_stop_multiplier} min="0" step="0.1" placeholder="e.g. 2.5" className="h-9" onChange={(e) => set('atr_stop_multiplier', e.target.value)} />
              </ConfigLabel>
              {config.atr_stop_multiplier != null && config.atr_stop_multiplier !== '' && <ConfigLabel label="ATR period">
                <Input type="number" value={config.atr_stop_period} min="2" max="100" className="h-9" onChange={(e) => set('atr_stop_period', e.target.value)} />
              </ConfigLabel>}
              {config.atr_stop_multiplier != null && config.atr_stop_multiplier !== '' && <div className="col-span-2">
                <ConfigLabel label="Mode">
                  <NativeSelect value={config.atr_stop_mode} onChange={(e) => set('atr_stop_mode', e.target.value)} className="h-9 py-1.5">
                    <option value="fixed">Fixed (from entry)</option>
                    <option value="trailing">Trailing (from peak)</option>
                  </NativeSelect>
                </ConfigLabel>
              </div>}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <h4 className="text-sm font-semibold text-foreground border-b border-border pb-1">Position sizing</h4>
            <ConfigLabel label="Method">
              <NativeSelect value={config.position_sizing} onChange={(e) => set('position_sizing', e.target.value)} className="h-9 py-1.5">
                {SIZING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </NativeSelect>
            </ConfigLabel>
            
            {config.position_sizing === 'fixed_amount' && (
              <ConfigLabel label="Amount (₹)">
                <Input type="number" value={config.position_size_value} min="0" className="h-9" onChange={(e) => set('position_size_value', e.target.value)} />
              </ConfigLabel>
            )}
            {config.position_sizing === 'fixed_pct' && (
              <ConfigLabel label="Percent (%)">
                <Input type="number" value={config.position_size_value} min="0" max="100" className="h-9" onChange={(e) => set('position_size_value', e.target.value)} />
              </ConfigLabel>
            )}
            {config.position_sizing === 'volatility' && (
              <div className="grid grid-cols-2 gap-3 p-3 bg-muted/30 rounded-lg border border-border/50">
                <div className="col-span-2">
                  <ConfigLabel label="Risk per trade (%)">
                    <Input type="number" value={config.position_size_value} min="0.1" max="100" step="0.1" placeholder="e.g. 1.0" className="h-9" onChange={(e) => set('position_size_value', e.target.value)} />
                  </ConfigLabel>
                </div>
                <ConfigLabel label="ATR period">
                  <Input type="number" value={config.vol_lookback} min="2" max="100" className="h-9" onChange={(e) => set('vol_lookback', e.target.value)} />
                </ConfigLabel>
                <ConfigLabel label="Multiplier">
                  <Input type="number" value={config.vol_atr_multiplier} min="0.1" step="0.1" className="h-9" onChange={(e) => set('vol_atr_multiplier', e.target.value)} />
                </ConfigLabel>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4">
            <h4 className="text-sm font-semibold text-foreground border-b border-border pb-1">Period</h4>
            <ConfigLabel label="Start date">
              <Input type="date" value={config.start_date} className="h-9" onChange={(e) => set('start_date', e.target.value)} />
            </ConfigLabel>
            <ConfigLabel label="End date">
              <Input type="date" value={config.end_date} className="h-9" onChange={(e) => set('end_date', e.target.value)} />
            </ConfigLabel>
          </div>

          <div className="sticky bottom-0 bg-card pt-4 pb-2 mt-auto border-t border-border/50 md:border-none md:pt-0">
            <Button onClick={handleRun} disabled={running} className="w-full h-10 font-semibold text-sm gap-2">
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" fill="currentColor" />}
              {running ? 'Running...' : 'Run Backtest'}
            </Button>
            {error && <div className="text-sm text-destructive mt-3 text-center bg-destructive/10 p-2 rounded-md">{error}</div>}
          </div>
        </aside>

        {/* Results Main Area */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 flex flex-col gap-8 bg-muted/10">
          
          {!result && !running && (
            <div className="flex flex-col items-center justify-center h-full text-center max-w-sm mx-auto gap-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Play className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">Ready to test</h2>
              <p className="text-sm text-muted-foreground">Adjust your parameters in the configuration panel and press Run to see how your strategy would have performed.</p>
              <Button onClick={() => setIsMobileConfigOpen(true)} className="md:hidden mt-2">Configure Strategy</Button>
            </div>
          )}

          {running && (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-muted-foreground">
              <img src="/analysis.svg" alt="" className="w-56 sm:w-64 max-w-full rounded-sm" />
              <p className="text-sm font-medium text-foreground min-h-[1.25rem]">{RUN_STAGES[runStage]}</p>
              <div className="w-full max-w-xs h-1.5 bg-muted/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-[width] duration-200 ease-out"
                  style={{ width: `${runPct}%` }}
                />
              </div>
            </div>
          )}

          {result && (<>
            {/* Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <Metric info="total_return_pct" label="Total return" value={pct(m.total_return_pct)} pos={m.total_return_pct > 0} neg={m.total_return_pct < 0} />
              <Metric
                /* Spans under a year are not extrapolated to an annual rate —
                   the figure is the period return, and says so. */
                info="cagr_pct"
                label={m.cagr_is_annualized === false ? 'Period return' : 'CAGR'}
                value={pct(m.cagr_pct)}
                sub={m.cagr_is_annualized === false ? `${m.period_years?.toFixed(2)}y — not annualised` : 'annualised'}
                pos={m.cagr_pct > 0} neg={m.cagr_pct < 0}
              />
              <Metric info="max_drawdown_pct" label="Max drawdown" value={`-${m.max_drawdown_pct?.toFixed(2)}%`} neg={m.max_drawdown_pct > 20} />
              <Metric
                info="sharpe_ratio"
                label="Sharpe ratio"
                value={m.sharpe_ratio?.toFixed(2) ?? '—'}
                /* The equity curve has a point per trading day whether or not
                   capital was deployed, so idle days drag this ratio toward a
                   measure of time in the market. The deployed-days figure
                   below it is the undistorted one. */
                sub={m.active_sharpe_ratio != null ? `${m.active_sharpe_ratio.toFixed(2)} on deployed days` : null}
                subInfo={m.active_sharpe_ratio != null ? 'active_sharpe_ratio' : null}
                pos={m.sharpe_ratio > 1} neg={m.sharpe_ratio < 0}
              />
              <Metric info="sortino_ratio" label="Sortino ratio" value={m.sortino_ratio?.toFixed(2) ?? '—'} pos={m.sortino_ratio > 1} neg={m.sortino_ratio < 0} />
              <Metric
                info="time_in_market_pct"
                label="Exposure"
                value={m.time_in_market_pct != null ? `${m.time_in_market_pct.toFixed(1)}%` : '—'}
                sub={m.avg_exposure_pct != null ? `${m.avg_exposure_pct.toFixed(1)}% avg deployed` : 'time in market'}
                subInfo={m.avg_exposure_pct != null ? 'avg_exposure_pct' : null}
                neg={m.time_in_market_pct != null && m.time_in_market_pct < 50}
              />
              <Metric
                info="expectancy_r"
                label="Expectancy"
                value={m.expectancy_r != null ? `${m.expectancy_r.toFixed(2)}R` : '—'}
                sub={m.expectancy_pct != null ? `${pct(m.expectancy_pct)} per trade` : null}
                pos={m.expectancy_r > 0} neg={m.expectancy_r < 0}
              />
              <Metric label="Final value" value={money(m.final_value)} sub={`from ${money(m.initial_funds)}`} pos={m.final_value > m.initial_funds} neg={m.final_value < m.initial_funds}/>
              <Metric info="win_rate_pct" label="Win rate" value={`${m.win_rate_pct?.toFixed(1)}%`} sub={`${m.total_trades} trades`} />
              <Metric info="profit_factor" label="Profit factor" value={m.profit_factor != null ? m.profit_factor.toFixed(2) : '—'} pos={m.profit_factor > 1} neg={m.profit_factor != null && m.profit_factor <= 1} />
              <Metric label="Avg holding" value={`${m.avg_holding_days} days`} />
            </div>

            {/* Equity Curve */}
            <Card className="p-4 rounded-xl shadow-xs border-border/60">
              <h4 className="text-base font-semibold text-foreground mb-4">Equity Curve</h4>
              <div className="w-full">
                <EquityChart data={result.equity_curve} initialFunds={m.initial_funds} height={250} />
              </div>
            </Card>

            {/* Performance Analytics */}
            {result.analytics && (<>

              {result.analytics.benchmark_metrics && (
                <BenchmarkPanel
                  metrics={result.analytics.benchmark_metrics}
                  portfolioReturnPct={m.total_return_pct}
                />
              )}
              
              {result.analytics.vix_curve?.length > 0 && (
                <Card className="p-4 rounded-xl shadow-xs border-border/60">
                  <div className="flex items-baseline justify-between mb-3">
                    <h4 className="text-base font-semibold text-foreground">India VIX</h4>
                    <span className="text-xs text-muted-foreground">amber &gt; 20 · red &gt; 25</span>
                  </div>
                  <VixChart data={result.analytics.vix_curve} height={160} />
                </Card>
              )}
              
              {result.analytics.monthly_returns?.length > 0 && (
                <Card className="p-4 rounded-xl shadow-xs border-border/60">
                  <h4 className="text-base font-semibold text-foreground mb-4">Monthly Returns</h4>
                  <MonthlyHeatmap data={result.analytics.monthly_returns} />
                </Card>
              )}

              {result.analytics.drawdown_curve?.length > 0 && (
                <Card className="p-4 rounded-xl shadow-xs border-border/60">
                  <h4 className="text-base font-semibold text-foreground mb-3">Drawdown</h4>
                  <DrawdownChart
                    data={result.analytics.drawdown_curve}
                    stats={result.analytics.drawdown_stats || {}}
                    height={160}
                  />
                </Card>
              )}

              {result.analytics.rolling_sharpe?.some(pt => pt.sharpe_3m != null) && (
                <Card className="p-4 rounded-xl shadow-xs border-border/60">
                  <h4 className="text-base font-semibold text-foreground mb-3">Rolling Sharpe</h4>
                  <RollingSharpChart data={result.analytics.rolling_sharpe} height={200} />
                </Card>
              )}
            </>)}

            {/* Trade Analysis */}
            {result.trades?.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="p-4 rounded-xl shadow-xs border-border/60">
                  <h4 className="text-base font-semibold text-foreground mb-3">Returns Distribution</h4>
                  <ReturnsHistogram data={returnsHistogram(result.trades)} />
                </Card>
                <Card className="p-4 rounded-xl shadow-xs border-border/60">
                  <h4 className="text-base font-semibold text-foreground mb-3">Trade Analysis by Exit Type</h4>
                  <ExitTypeAnalysis data={exitTypeStats(result.trades)} />
                </Card>
              </div>
            )}

            {/* Trades Table */}
            <Card className="flex flex-col gap-0 rounded-xl shadow-xs border-border/60 overflow-hidden min-h-[400px]">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-border/60 bg-card">
                <h4 className="text-base font-semibold text-foreground">Trade History</h4>
                <div className="flex items-center gap-4 text-xs">
                  <span className="bg-muted px-2 py-1 rounded-md text-foreground font-medium">{sortedTrades.length} Total</span>
                  <span className="bg-win/10 text-win px-2 py-1 rounded-md font-medium">{sortedTrades.filter((t) => (t.pnl ?? 0) > 0).length} Won</span>
                  <span className="bg-loss/10 text-loss px-2 py-1 rounded-md font-medium">{sortedTrades.filter((t) => (t.pnl ?? 0) <= 0).length} Lost</span>
                </div>
              </div>
              
              <div className="overflow-x-auto w-full">
                <table className="w-full text-sm border-collapse min-w-[800px]">
                  <thead>
                    <tr className="bg-muted/30">
                      {[
                        ['entry_date',  'Entry'],
                        ['symbol',      'Symbol'],
                        ['entry_price', 'Entry ₹'],
                        ['exit_date',   'Exit'],
                        ['exit_price',  'Exit ₹'],
                        ['shares',      'Shares'],
                        ['pnl',         'P&L ₹'],
                        ['pnl_pct',     'Return'],
                        ['holding_days','Days'],
                        ['exit_reason', 'Reason'],
                      ].map(([col, lbl]) => (
                        <th key={col} onClick={() => onSort(col)} className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground cursor-pointer hover:text-foreground whitespace-nowrap select-none">
                          {lbl}{sortIcon(col)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedTrades.map((t, i) => (
                      <tr key={i} className="border-b border-border/30 hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3 text-xs text-muted-foreground">{t.entry_date}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-foreground">{t.symbol.replace('.NS', '')}</td>
                        <td className="px-4 py-3 text-xs text-right text-muted-foreground">{t.entry_price?.toFixed(2)}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">{t.exit_date ?? '—'}</td>
                        <td className="px-4 py-3 text-xs text-right text-muted-foreground">{t.exit_price?.toFixed(2) ?? '—'}</td>
                        <td className="px-4 py-3 text-xs text-right font-medium text-foreground">{t.shares}</td>
                        <td className={`px-4 py-3 text-xs text-right font-semibold ${(t.pnl ?? 0) > 0 ? 'text-win' : (t.pnl ?? 0) < 0 ? 'text-loss' : 'text-muted-foreground'}`}>
                          {t.pnl != null ? `${t.pnl > 0 ? '+' : ''}${t.pnl.toFixed(0)}` : '—'}
                        </td>
                        <td className={`px-4 py-3 text-xs text-right font-semibold ${(t.pnl_pct ?? 0) > 0 ? 'text-win' : (t.pnl_pct ?? 0) < 0 ? 'text-loss' : 'text-muted-foreground'}`}>
                          {t.pnl_pct != null ? pct(t.pnl_pct) : '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-right text-muted-foreground">{t.holding_days}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground capitalize">{t.exit_reason.replace(/_/g, ' ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-border/60 bg-card">
                  <span className="text-xs text-muted-foreground hidden sm:block">Showing {tradePage * TRADES_PER_PAGE + 1} to {Math.min((tradePage + 1) * TRADES_PER_PAGE, sortedTrades.length)} of {sortedTrades.length}</span>
                  <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
                    <Button variant="outline" size="sm" disabled={tradePage === 0} onClick={() => setTradePage((p) => p - 1)}>← Prev</Button>
                    <span className="text-xs font-medium text-foreground sm:hidden">{tradePage + 1} / {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={tradePage === totalPages - 1} onClick={() => setTradePage((p) => p + 1)}>Next →</Button>
                  </div>
                </div>
              )}
            </Card>
          </>)}
        </main>
      </div>
    </div>
  );
}